import PropTypes from 'prop-types';
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip,
} from 'recharts';

/**
 * Skill profile as a radar. A participant's own report passes ONE series (D3: no
 * partner overlay); the trainer view may pass the room average plus up to two people.
 *
 * Unmeasured skills (null) plot at 0 and say "Not measured" in the tooltip.
 */

/** Axis label split over up to two lines, so long skill names fit on a phone. */
const WrappedTick = ({ x, y, cx, cy, payload, textAnchor }) => {
  const words = String(payload.value).split(' ');
  const half = Math.ceil(words.length / 2);
  const lines = words.length > 1 ? [words.slice(0, half).join(' '), words.slice(half).join(' ')] : [payload.value];
  // Top labels grow upwards, bottom labels downwards, side labels around the point.
  const extra = (lines.length - 1) * 13;
  let first = 4 - extra / 2;
  if (y < cy - 5) first = -extra;
  else if (y > cy + 5) first = 10;
  return (
    <text x={x} y={y} textAnchor={textAnchor} fontSize={11} fill="#525252">
      {lines.map((line, i) => (
        <tspan key={line} x={x} dy={i === 0 ? first : 13}>{line}</tspan>
      ))}
    </text>
  );
};
WrappedTick.propTypes = {
  x: PropTypes.number, y: PropTypes.number, cx: PropTypes.number, cy: PropTypes.number,
  payload: PropTypes.object, textAnchor: PropTypes.string,
};

const SkillRadar = ({ skills, series, height = 260 }) => {
  if (skills.length < 3) return null;
  const data = skills.map((s) => {
    const row = { skill: s.label, fullLabel: s.label };
    series.forEach((ser, i) => {
      const v = ser.values[s.key];
      row[`s${i}`] = v === null || v === undefined ? 0 : v;
      row[`raw${i}`] = v;
    });
    return row;
  });

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <RadarChart data={data} outerRadius="68%" margin={{ top: 16, right: 28, bottom: 16, left: 28 }}>
          <PolarGrid stroke="#E5E7EB" />
          <PolarAngleAxis dataKey="skill" tick={<WrappedTick />} />
          <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
          {series.map((ser, i) => (
            <Radar
              key={ser.name}
              name={ser.name}
              dataKey={`s${i}`}
              stroke={ser.color}
              fill={ser.color}
              fillOpacity={series.length > 1 ? 0.12 : 0.25}
              strokeWidth={2}
              dot={{ r: 3, fill: ser.color }}
              isAnimationActive={false}
            />
          ))}
          <Tooltip
            formatter={(value, name, item) => {
              const i = Number(String(item.dataKey).slice(1));
              const raw = item.payload[`raw${i}`];
              return [raw === null || raw === undefined ? 'Not measured' : `${raw}%`, name];
            }}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

SkillRadar.propTypes = {
  skills: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })).isRequired,
  series: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    values: PropTypes.object.isRequired, // { skillKey: 0–100 | null }
  })).isRequired,
  height: PropTypes.number,
};

export default SkillRadar;
